import React, { useMemo, useCallback } from "react";
import "./KonversiMaterial.css";
import { useKonversiMaterial } from "./useKonversiMaterial";
import { bulan } from "../../util/constant";
import Chart from "react-apexcharts";
import PopUpComponent from "../../components/PopUp/PopUpComponent";
import CloseLogo from "../../Assets/close_icon.png";
import moment from "moment";
import ToggleOnOf from "../../components/ToggleOnOf/ToggleOnOf";
import {
  tahun,
  rejectColumns,
  rejectPR,
  rejectSP,
  constCustomer,
} from "../../util/constant";

const SUB_MENUS = [
  { key: "Injection", label: "Injection" },
  { key: "Pasang Sticker", label: "Pasang Sticker" },
  { key: "PR", label: "Printing" },
  { key: "SP", label: "Finishing" },
  { key: "Karantina", label: "Karantina" },
];

const KonversiMaterial = () => {
  const {
    dataUser,
    sortData,
    filterMonth,
    setFilterMonth,
    filterYear,
    setFilterYear,
    handleReset,
    findSameGrandTotalReject,
    handlePickReject,
    popUpDetailReject,
    setPopUpDetailReject,
    dataTempDetailProduk,
    titleDetail,
    formatDot,
    numberWithCommas,
    switchDotsAndCommas,
    toggle,
    setToggle,
    filterShift,
    setFilterShift,
    popUpFilterRange,
    setPopUpFilterRange,
    filterFrom,
    setFilterFrom,
    filterUntil,
    setFilterUntil,
    setIsFilterRange,
    setIsRefresh,
    filterJenis,
    setFilterJenis,
    subMenu,
    setSubMenu,
    buildPrSpChartData,
    filterPrinting,
    setFilterPrinting,
    TOP_N,
    filterCustomer,
    setFilterCustomer,
    grandTotalOk,
  } = useKonversiMaterial();

  const isInjection = subMenu === "Injection" || subMenu === "Karantina";

  const rejectColumnsReguler = useMemo(
    () => (subMenu === "PR" ? rejectPR : subMenu === "SP" ? rejectSP : []),
    [subMenu]
  );

  const rejectColumnsIdSet = useMemo(
    () => new Set(rejectColumns.map((r) => r.id)),
    []
  );

  const activeRejectColumns = useMemo(
    () =>
      isInjection
        ? rejectColumns
        : [
            ...rejectColumns,
            ...rejectColumnsReguler
              .filter((r) => !rejectColumnsIdSet.has(r.desc))
              .map((r) => ({
                id: r.desc,
                label: r.desc,
              })),
          ],
    [isInjection, rejectColumnsReguler, rejectColumnsIdSet]
  );

  const safeNumber = (val) => (isNaN(val) || !isFinite(val) ? 0 : val);

  const showData = useMemo(() => {
    if (!findSameGrandTotalReject) return [];
    const sorted = [...findSameGrandTotalReject].sort(
      (a, b) => b.totalNG - a.totalNG
    );
    return sorted.slice(0, TOP_N).map((el) => el.id);
  }, [findSameGrandTotalReject, TOP_N]);

  const showDataSet = useMemo(() => new Set(showData), [showData]);
  const findShowData = useCallback(
    (filter) => showDataSet.has(filter),
    [showDataSet]
  );

  const filteredData = useMemo(() => {
    return (sortData || [])
      .map((item) => {
        const perincian = Array.isArray(item.perincianNg)
          ? item.perincianNg
          : [];

        const totalNg = perincian.reduce(
          (sum, x) => sum + Number(x?.totalNG || 0),
          0
        );

        const berat = Number(item.berat || 0);

        const total = toggle ? totalNg : (totalNg * berat) / 1000;

        return {
          ...item,
          total: Number.isFinite(total) ? total : 0,
        };
      })
      .sort((a, b) => {
        const diff = b.total - a.total;
        if (diff !== 0) return diff;
        return String(a.name).localeCompare(String(b.name));
      })
      .slice(0, TOP_N);
  }, [sortData, toggle, TOP_N]);

  const generateColors = useCallback((count) => {
    return Array.from({ length: count }, (_, i) => {
      const hue = (i * 137.508) % 360;
      return `hsl(${hue}, 70%, 50%)`;
    });
  }, []);

  const categoriesInjection = useMemo(
    () => filteredData?.map((item) => `${item.name} - ${item.color}`) || [],
    [filteredData]
  );

  const chartSeriesInjection = useMemo(() => {
    const raw = rejectColumns.map((reject) => ({
      name: reject.label,
      data: categoriesInjection.map((produk) => {
        const item = sortData?.find((x) => `${x.name} - ${x.color}` === produk);
        if (!item) return 0;
        const ngValue =
          item.perincianNg
            ?.filter((r) => r.id === reject.id)
            ?.reduce((a, b) => a + Number(b.totalNG || 0), 0) || 0;
        return toggle
          ? ngValue
          : reject.id === "Cuci Warna"
          ? (item.berat * 0.4 * ngValue) / 1000
          : (item.berat * ngValue) / 1000;
      }),
    }));

    return subMenu === "Karantina"
      ? raw
      : raw.filter((s) => s.data.some((v) => Number(v) > 0));
  }, [categoriesInjection, sortData, toggle, subMenu]);

  const filteredPrSpData = useMemo(() => {
    return (sortData || [])
      .map((item) => {
        const totalNg = (item.perincianNg || []).reduce(
          (sum, x) => sum + Number(x?.totalNG || 0),
          0
        );
        const berat = Number(item.berat || 0);
        const total = toggle ? totalNg : (totalNg * berat) / 1000;
        return { ...item, _total: Number.isFinite(total) ? total : 0 };
      })
      .sort((a, b) => b._total - a._total)
      .slice(0, TOP_N);
  }, [sortData, toggle, TOP_N]);

  const { categories: categoriesPrSp, series: chartSeriesPrSp } = useMemo(
    () => buildPrSpChartData(filteredPrSpData),
    [buildPrSpChartData, filteredPrSpData]
  );

  const buildChartOptions = useCallback(
    (categories, series, title) => ({
      chart: {
        id: "stacked-column",
        type: "bar",
        stacked: true,
        toolbar: { show: true },
        animations: {
          enabled: true,
          easing: "linear",
          speed: 100,
          animateGradually: { enabled: false },
          dynamicAnimation: { enabled: true, speed: 100 },
        },
        redrawOnParentResize: true,
        redrawOnWindowResize: false,
        events: {
          mounted: (chart) => {
            chart.windowResizeHandler();
          },
        },
      },

      colors: generateColors(Math.max(series.length, 1)),

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadius: 4,
        },
      },

      dataLabels: { enabled: false },

      annotations: {
        position: "front",
        points: categories.map((_, i) => {
          let total = 0;
          series.forEach((s) => {
            total += Number(s.data[i]) || 0;
          });
          return {
            x: categories[i],
            y: total,
            marker: { size: 0 },
            label: {
              text: total.toFixed(0),
              offsetY: 6,
              style: { fontSize: "24px" },
            },
          };
        }),
      },

      stroke: { width: 1, colors: ["#fff"] },

      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: false,
          style: { fontSize: "14px" },
        },
      },

      yaxis: {
        title: { text: toggle ? "Reject (Pcs)" : "Reject (Kg)" },
        labels: {
          style: { fontSize: "14px" },
          formatter: (val) => Number(val || 0).toFixed(0),
        },
      },

      legend: { position: "right", fontSize: "13px" },

      fill: { opacity: 1 },

      grid: {
        padding: { left: 80, right: 20, bottom: 80 },
      },

      title: {
        text: title,
        style: { fontSize: "28px", fontWeight: "bold" },
      },

      tooltip: {
        fixed: {
          enabled: true,
          position: "topRight",
          offsetX: 0,
          offsetY: -50,
        },
        shared: true,
        intersect: false,

        custom: function ({ series: s, dataPointIndex, w }) {
          const lbl = w.globals.labels[dataPointIndex];
          const unit = toggle ? "Pcs" : "Kg";
          const decimals = toggle ? 0 : 2;

          let items = [];
          s.forEach((sv, i) => {
            const value = Number(sv[dataPointIndex] || 0);
            if (value > 0) {
              items.push({
                name: w.globals.seriesNames[i],
                value,
                color: w.globals.colors[i],
              });
            }
          });

          items.sort((a, b) => b.value - a.value);
          const total = items.reduce((sum, i) => sum + i.value, 0);

          return `
            <div style="
              padding:10px;
              font-size:14px;
              line-height:1.2;
              background:white;
              border-radius:6px;
              width:max-content;
              min-width:300px;
            ">
              <div style="
                font-size:16px;
                font-weight:700;
                background:#008FFB;
                color:white;
                padding:6px 8px;
                border-radius:4px;
                margin-bottom:6px;
              ">
                ${lbl}
              </div>
              <div style="font-size:14px; margin-bottom:6px;">
                Total Reject : <b>${Number(total).toFixed(decimals)} ${unit}</b>
              </div>
              ${items
                .map(
                  (i) => `
                <div style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  font-size:15px;
                  padding:4px 0;
                ">
                  <div style="display:flex; align-items:center; gap:6px;">
                    <span style="
                      width:10px; height:10px;
                      background:${i.color};
                      border-radius:2px;
                      display:inline-block;
                    "></span>
                    <span>${i.name}</span>
                  </div>
                  <b>${Number(i.value).toFixed(decimals)} ${unit}</b>
                </div>
              `
                )
                .join("")}
            </div>
          `;
        },
      },

      noData: {
        text: "Loading...",
        style: { fontSize: "36px" },
      },
    }),
    [generateColors, toggle]
  );

  const activeCategories = isInjection ? categoriesInjection : categoriesPrSp;
  const activeSeries = isInjection ? chartSeriesInjection : chartSeriesPrSp;
  const activeTitle = isInjection
    ? `Reject berdasarkan Produk (${toggle ? "Pcs" : "Kg"})`
    : ` Kelolosan & Reject per Produk (${toggle ? "Pcs" : "Kg"})`;

  const templateData = useMemo(
    () => ({
      options: buildChartOptions(activeCategories, activeSeries, activeTitle),
      series: activeSeries,
    }),
    [buildChartOptions, activeCategories, activeSeries, activeTitle]
  );

  const find = useCallback(
    (value) => {
      const wow =
        findSameGrandTotalReject &&
        findSameGrandTotalReject.find((x) => x.id === value);
      return formatDot(
        switchDotsAndCommas(numberWithCommas(wow && wow.totalNG))
      );
    },
    [findSameGrandTotalReject, formatDot, switchDotsAndCommas, numberWithCommas]
  );

  const sumReject = useMemo(() => {
    return (
      findSameGrandTotalReject &&
      findSameGrandTotalReject.reduce(function (s, a) {
        return Number(s) + Number(a.totalNG);
      }, 0)
    );
  }, [findSameGrandTotalReject]);

  const getTotalRejectValue = useCallback(
    (data) => {
      const findSameReject =
        data &&
        data.perincianNg.reduce((obj, item) => {
          let find = obj.find((i) => i.id === item.id && i.totalNG !== "");
          let _d = { ...item };
          if (find) {
            find.id = item.id;
            find.totalNG = Number(find.totalNG) + Number(item.totalNG);
          } else {
            obj.push(_d);
          }
          return obj;
        }, []);

      let tempNg = 0;
      findSameReject &&
        findSameReject.forEach((element) => {
          if (element.totalNG) {
            if (toggle) {
              tempNg += Number(element.totalNG);
            } else {
              if (element.id === "Inject Kosong") {
              } else if (element.id === "Cuci Warna") {
                tempNg += (data.berat * 0.4 * element.totalNG) / 1000;
              } else {
                tempNg += (data.berat * element.totalNG) / 1000;
              }
            }
          }
        });

      return tempNg;
    },
    [toggle]
  );

  const sortedTableData = useMemo(() => {
    return (
      sortData &&
      [...sortData].sort(
        (a, b) => getTotalRejectValue(b) - getTotalRejectValue(a)
      )
    );
  }, [sortData, getTotalRejectValue]);

  const unitLabel = toggle ? "Pcs" : "Kg";

  return (
    <div className="container_dashboard km_root">
      {/* ===== Popup detail reject ===== */}
      {popUpDetailReject ? (
        <PopUpComponent scroll>
          <div className="km_popup">
            <div className="km_popup_head">
              <p className="km_popup_title">
                {titleDetail.name} - {titleDetail.color}
                <br />
                <b>{titleDetail.jenis}</b>
              </p>
              <button
                type="button"
                className="km_icon_button"
                aria-label="Tutup"
                onClick={() => setPopUpDetailReject(false)}
              >
                <img src={CloseLogo} alt="" />
              </button>
            </div>

            {dataTempDetailProduk[0] && dataTempDetailProduk[0].all ? (
              <div>
                {dataTempDetailProduk &&
                  dataTempDetailProduk.map((data, index) => {
                    const findReject =
                      data &&
                      data.perincianNg.find((o) => o.id === titleDetail.jenis);
                    let berat = data.berat ? data.berat : 0;
                    let calculate = toggle
                      ? findReject?.totalNG || 0
                      : (berat * (findReject?.totalNG || 0)) / 1000;

                    return (
                      <p key={index} className="km_detail_total">
                        Total Reject :&nbsp;
                        {formatDot(
                          switchDotsAndCommas(numberWithCommas(calculate))
                        )}{" "}
                        {unitLabel}
                      </p>
                    );
                  })}
              </div>
            ) : (
              <div className="km_detail_scroll">
                <table className="km_detail_table">
                  <thead>
                    <tr>
                      <th>Tgl Prod</th>
                      <th>Produk</th>
                      <th>Warna</th>
                      <th>Reject {toggle ? "(Pcs)" : "(Kg)"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataTempDetailProduk &&
                      dataTempDetailProduk.map((data, index) => {
                        const findReject =
                          data &&
                          data.perincianNg.find(
                            (o) => o.id === titleDetail.jenis
                          );
                        let berat = data.berat ? data.berat : 0;
                        let calculate =
                          titleDetail.jenis === "Inject Kosong"
                            ? findReject?.totalNG || 0
                            : titleDetail.jenis === "Cuci Warna"
                            ? (berat * (findReject?.totalNG || 0) * 0.4) / 1000
                            : (berat * (findReject?.totalNG || 0)) / 1000;

                        return (
                          <tr key={index}>
                            <td>{moment(data.date).format("DD-MM-YY")}</td>
                            <td>{data.name}</td>
                            <td>{data.color}</td>
                            <td>
                              {toggle
                                ? formatDot(
                                    switchDotsAndCommas(
                                      numberWithCommas(findReject?.totalNG || 0)
                                    )
                                  )
                                : formatDot(
                                    switchDotsAndCommas(
                                      numberWithCommas(calculate)
                                    )
                                  )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </PopUpComponent>
      ) : (
        ""
      )}

      {/* ===== Popup filter range ===== */}
      {popUpFilterRange ? (
        <PopUpComponent scroll>
          <div className="km_popup">
            <div className="km_popup_head">
              <p className="km_popup_title">
                <b>Filter Rentang Tanggal</b>
              </p>
              <button
                type="button"
                className="km_icon_button"
                aria-label="Tutup"
                onClick={() => setPopUpFilterRange(false)}
              >
                <img src={CloseLogo} alt="" />
              </button>
            </div>
            <div className="km_range_row">
              <div className="km_range_field">
                <label className="km_range_label" htmlFor="km_from">
                  Dari<span className="km_required">*</span>
                </label>
                <input
                  id="km_from"
                  type="date"
                  className="km_input"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                />
              </div>
              <div className="km_range_field">
                <label className="km_range_label" htmlFor="km_until">
                  Sampai<span className="km_required">*</span>
                </label>
                <input
                  id="km_until"
                  type="date"
                  className="km_input"
                  value={filterUntil}
                  onChange={(e) => setFilterUntil(e.target.value)}
                />
              </div>
            </div>
            <div className="km_popup_actions">
              <button
                type="button"
                className="km_button km_button_primary"
                onClick={() => {
                  setIsFilterRange(true);
                  setIsRefresh(true);
                  setPopUpFilterRange(false);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </PopUpComponent>
      ) : (
        ""
      )}

      {/* ===== Judul + tab ===== */}
      <p className="title_inventory km_title">Rejection Report</p>

      <div className="km_tabs" role="tablist">
        {SUB_MENUS.map((menu) => (
          <button
            key={menu.key}
            type="button"
            role="tab"
            aria-selected={subMenu === menu.key}
            className={subMenu === menu.key ? "km_tab km_tab_active" : "km_tab"}
            onClick={() => setSubMenu(menu.key)}
          >
            {menu.label}
          </button>
        ))}
      </div>

      {/* ===== Filter ===== */}
      <div className="km_filters">
        <select
          onChange={(e) => setFilterMonth(e.target.value)}
          className="km_input"
          value={filterMonth}
        >
          <option disabled>Pilih Bulan</option>
          {bulan &&
            bulan.map((data) => (
              <option key={data.value} value={data.value}>
                {data.name}
              </option>
            ))}
        </select>
        <select
          onChange={(e) => setFilterYear(e.target.value)}
          className="km_input"
          value={filterYear}
        >
          <option disabled>Pilih Tahun</option>
          {tahun.map((ele) => (
            <option key={ele} value={ele}>
              {ele}
            </option>
          ))}
        </select>

        {dataUser.tim === 1 && subMenu === "Injection" ? (
          <>
            <select
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="km_input"
              value={filterCustomer}
            >
              <option disabled>Pilih Customer</option>
              {constCustomer.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
            <select
              onChange={(e) => setFilterShift(e.target.value)}
              className="km_input"
              value={filterShift}
            >
              <option disabled>Pilih Shift</option>
              <option value="SUPRIADI">SUPRIADI</option>
              <option value="SARIPUDIN">SARIPUDIN</option>
              <option value="ACHMAD">ACHMAD</option>
            </select>
          </>
        ) : (
          ""
        )}

        {isInjection && subMenu === "Injection" && (
          <select
            onChange={(e) => setFilterJenis(e.target.value)}
            className="km_input"
            value={filterJenis}
          >
            <option disabled>Pilih Jenis</option>
            <option value="CONT">CONTAINER</option>
            <option value="COV">COVER</option>
            <option value="HANDLE">HANDLE</option>
            <option value="PACKER">PACKER</option>
            <option value="SEALING CAP">SEALING CAP</option>
            <option value="TERMINAL CAP">TERMINAL CAP</option>
            <option value="TERMINAL COVER">TERMINAL COVER</option>
            <option value="TERMINAL RESIN">TERMINAL RESIN</option>
            <option value="VP">VP</option>
          </select>
        )}
        {subMenu === "PR" && (
          <select
            onChange={(e) => setFilterPrinting(e.target.value)}
            className="km_input"
            value={filterPrinting}
          >
            <option disabled>Pilih Jenis</option>
            <option value="Rejection">Rejection</option>
            <option value="Repair">Repair</option>
          </select>
        )}

        <button
          type="button"
          className="km_button km_button_green"
          onClick={() => setPopUpFilterRange(true)}
        >
          Range
        </button>
        <button
          type="button"
          className="km_button km_button_outline"
          onClick={() => handleReset()}
        >
          Reset
        </button>
      </div>

      {/* ===== Ringkasan ===== */}
      <div className="km_summary">
        <div className="km_summary_total">
          <p className="km_summary_label">Total Reject</p>
          <span className="km_summary_value">
            {formatDot(switchDotsAndCommas(numberWithCommas(sumReject)))}{" "}
            {unitLabel}
          </span>
          {sumReject && grandTotalOk ? (
            <span className="km_summary_percent">
              {`${((sumReject / grandTotalOk) * 100).toFixed(1)}%`}
            </span>
          ) : (
            ""
          )}
        </div>
        <div className="km_unit_switch">
          <span>Satuan: {unitLabel}</span>
          <ToggleOnOf
            onClick={() => (toggle ? setToggle(false) : setToggle(true))}
            value={toggle}
          />
        </div>
      </div>

      {!isInjection && (
        <div className="km_note">
          <span>
            <b style={{ color: "#e44" }}>*</b> Series dengan format{" "}
            <b>"Shift - Jenis Reject"</b> = Kelolosan dari Injection
          </span>
          <span>
            <b style={{ color: "#44a" }}>*</b> Series hanya{" "}
            <b>"Jenis Reject"</b> = Reject dari proses {subMenu} sendiri
          </span>
        </div>
      )}

      {/* ===== Chart ===== */}
      <div className="km_card km_chart_card">
        {activeSeries && activeSeries.length > 0 ? (
          <Chart
            options={templateData.options}
            series={templateData.series}
            type="bar"
            width="100%"
            height={700}
          />
        ) : (
          <div className="km_empty">Tidak ada data untuk ditampilkan</div>
        )}
      </div>

      {/* ===== Tabel ===== */}
      <div className="km_card">
        <div className="km_table_scroll">
          <table className="km_table">
            <thead>
              <tr>
                <th className="km_sticky km_sticky_no">No.</th>
                <th className="km_sticky km_sticky_name">Produk</th>
                <th>Warna</th>
                <th>Total OK {toggle ? "(Pcs)" : "(Kg)"}</th>
                <th>Total Reject {toggle ? "(Pcs)" : "(Kg)"}</th>
                <th>% Reject</th>
                {activeRejectColumns.map((column) =>
                  findShowData(column.id) ? (
                    <th key={column.id} className="km_th_reject">
                      {column.label}
                    </th>
                  ) : null
                )}
              </tr>
            </thead>

            <tbody>
              {sortedTableData &&
                sortedTableData.map((data, index) => {
                  const findSameReject =
                    data &&
                    data.perincianNg.reduce((obj, item) => {
                      let find = obj.find(
                        (i) => i.id === item.id && i.totalNG !== ""
                      );
                      let _d = { ...item };
                      if (find) {
                        find.id = item.id;
                        find.totalNG =
                          Number(find.totalNG) + Number(item.totalNG);
                      } else {
                        obj.push(_d);
                      }
                      return obj;
                    }, []);

                  const calculateKindOfReject = (filter, type) => {
                    if (type === "kind") {
                      if (toggle) {
                        let tempNg;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.id === filter) {
                              tempNg = Number(element.totalNG);
                            }
                          });

                        const calculate = tempNg;

                        return calculate
                          ? formatDot(
                              switchDotsAndCommas(numberWithCommas(calculate))
                            )
                          : "";
                      } else {
                        let tempNg;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.id === filter) {
                              tempNg = Number(element.totalNG);
                            }
                          });

                        const calculate =
                          filter === "Inject Kosong"
                            ? tempNg
                            : filter === "Cuci Warna"
                            ? (data.berat * 0.4 * tempNg) / 1000
                            : (data.berat * tempNg) / 1000;
                        return calculate
                          ? formatDot(
                              switchDotsAndCommas(numberWithCommas(calculate))
                            )
                          : "";
                      }
                    } else if (type === "persentase") {
                      if (toggle) {
                        let tempNg = 0;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.totalNG) {
                              tempNg += Number(element.totalNG);
                            }
                          });
                        return tempNg;
                      } else {
                        let tempNg = 0;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.totalNG) {
                              if (element.id === "Inject Kosong") {
                              } else if (element.id === "Cuci Warna") {
                                tempNg += data.berat * 0.4 * element.totalNG;
                              } else {
                                tempNg += data.berat * element.totalNG;
                              }
                            }
                          });
                        return tempNg;
                      }
                    } else {
                      if (toggle) {
                        let tempNg = 0;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.totalNG) {
                              tempNg += Number(element.totalNG);
                            }
                          });
                        return tempNg !== 0
                          ? formatDot(
                              switchDotsAndCommas(numberWithCommas(tempNg))
                            )
                          : "";
                      } else {
                        let tempNg = 0;
                        findSameReject &&
                          findSameReject.forEach((element) => {
                            if (element.totalNG) {
                              if (element.id === "Inject Kosong") {
                              } else if (element.id === "Cuci Warna") {
                                tempNg +=
                                  (data.berat * 0.4 * element.totalNG) / 1000;
                              } else {
                                tempNg += (data.berat * element.totalNG) / 1000;
                              }
                            }
                          });
                        return tempNg !== 0
                          ? formatDot(
                              switchDotsAndCommas(numberWithCommas(tempNg))
                            )
                          : "";
                      }
                    }
                  };

                  let totalNgPersentase = calculateKindOfReject(
                    "",
                    "persentase"
                  );

                  const denominator =
                    totalNgPersentase +
                    (data.jumlahBarang || 0) * (data.berat || 0);

                  let calculatePersen =
                    denominator === 0
                      ? 0
                      : (totalNgPersentase / denominator) * 100;

                  calculatePersen = safeNumber(calculatePersen);

                  return (
                    <tr key={index}>
                      <td className="km_sticky km_sticky_no">{index + 1}.</td>
                      <td className="km_sticky km_sticky_name">{data.name}</td>
                      <td>{data.color}</td>
                      <td>
                        {toggle
                          ? formatDot(
                              switchDotsAndCommas(
                                numberWithCommas(data.jumlahBarang)
                              )
                            )
                          : formatDot(
                              switchDotsAndCommas(
                                numberWithCommas(
                                  (data.jumlahBarang * data.berat) / 1000
                                )
                              )
                            )}
                      </td>
                      <td
                        className="km_cell_click"
                        onClick={() => handlePickReject(data, "", "all")}
                      >
                        {calculateKindOfReject("", "all")}
                      </td>
                      <td>
                        {formatDot(
                          switchDotsAndCommas(numberWithCommas(calculatePersen))
                        )}
                      </td>
                      {activeRejectColumns.map(
                        (col) =>
                          findShowData(col.id) && (
                            <td
                              key={col.id}
                              className="km_cell_click"
                              onClick={() => handlePickReject(data, col.id)}
                            >
                              {calculateKindOfReject(col.id, "kind")}
                            </td>
                          )
                      )}
                    </tr>
                  );
                })}
            </tbody>

            <tfoot>
              <tr>
                <td className="km_sticky km_sticky_no"></td>
                <td className="km_sticky km_sticky_name km_grand_label">
                  GRAND TOTAL :
                </td>
                <td colSpan={4}></td>
                {activeRejectColumns.map(
                  (col) =>
                    findShowData(col.id) && <td key={col.id}>{find(col.id)}</td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KonversiMaterial;
